import React, { useState } from "react";
import { ICreditResponse, ISummaryItems } from "../../../store/credit/types";
import { formatCurrency } from "../../../util/credit";
import { ApplicationState } from "../../../store";
import { useDispatch, useSelector } from "react-redux";
import { getDate } from "../../../util/date";
import { getServiceKey } from "../../../util/credit";
import { getActiveServiceCode } from "../../../util/CommonServices";
import * as httpClient from "../../../util/httpClient";
import * as CreditActions from "../../../store/credit/actions";
import ReactDOMServer from "react-dom/server";
import PrintCreditReport from "./Print/PrintCreditReport";
import { useBureauPermission, useBureauPermissionCreditReport } from "../../../util/credit";
import CreditReportExpired from "../CreditReportExpired";
import CreditReportNavigation from "./CreditReportNavigation";
import CreditStatus from "../CreditStatus";
import { getFormattedDate } from "../../../util/dateFormatting";
import i18nService from "../../../util/Internationalization/i18n/i18n";
import RenderIcons from "../../../Assets/css/_icons/partner_icon/RenderIcons";
import { ezsServiceIds, idfServiceIds } from "../../../config/UserServicesList";
import { useAreServicesActive } from "../../../util/CommonServices";
import * as UserServiceAction from "../../../store/userService/actions";
import { useTranslation } from "react-i18next";

interface IProps {
    data?: ICreditResponse;
    isTUUser?: boolean;
    view: string;
    toggleReportView?: any;
    isOneTimeReport?: boolean;
}

const CreditSummary: React.FunctionComponent<IProps> = (props) => {
    const dispatch = useDispatch();
    const { t: tLang } = useTranslation();
    const bureauPermission = useBureauPermission();
    const reportServiceCode = props.data && props.data["service-code"] ? props.data["service-code"] : "";
    const scoreOnlyIds = [
        idfServiceIds.creditScoreMonitoring,
        idfServiceIds.threeBureauCreditScores,
        idfServiceIds.transUnionCreditScore,
        ezsServiceIds.quarterlyCreditScore,
        ezsServiceIds.tuDailyCreditScore,
        ezsServiceIds.tuMonthlyCreditScore,
        ezsServiceIds.tuQuarterlyCreditScore,
        ezsServiceIds.tuAnnualCreditScore,
        ezsServiceIds.tuCanadaCreditScore,
        ezsServiceIds.monthlyCreditScore,
        ezsServiceIds.tuCreditScoreTracker,
        ezsServiceIds.tuCanadaCreditScoreTracker,
        ezsServiceIds.expMonthlyCreditScoreTracker,
    ];
    const hasCreditScoreOnlyService = useAreServicesActive(scoreOnlyIds);
    const prioritizedServiceCode = useSelector((state: ApplicationState) => {
        if (state.creditHistoryData.isOneTimeOnlyScore && !hasCreditScoreOnlyService && !state.creditHistoryData.isOneTimeReportLatest) {
            return state.creditHistoryData.oneTimeScoreServiceCode;
        } else if (state.creditHistoryData.isOneTimeOnlyReport) {
            return state.creditHistoryData.oneTimeReportServiceCode;
        } else {
            return "";
        }
    });
    const partnerPhoneNumber: string = useSelector((state: ApplicationState) =>
        state.configurationState && state.configurationState.configuration.partnerConfig.phoneNumber
            ? state.configurationState.configuration.partnerConfig.phoneNumber
            : ""
    );
    const requestCreditData = (forceRefresh: boolean) =>
        dispatch(CreditActions.actionCreators.requestCreditData(forceRefresh, bureauPermission, prioritizedServiceCode));
    const refreshCredit = () => dispatch(CreditActions.actionCreators.refreshCredit(true, refreshCallback, reportServiceCode));
    const [isBusy, setIsBusy] = useState(false);
    const [isJustRefreshed, setIsJustRefreshed] = useState(false);
    const [nextPullInYears, setNextPullInYears] = useState(-1);
    const isTUUser = useSelector((state: ApplicationState) => state.loggedInUser && state.loggedInUser.userInfo?.isTUUser);
    const getUserService = (isForceCall: boolean) => dispatch(UserServiceAction.actionCreators.requestUserService(isForceCall));

    const oneTimeCreditServiceIds = [
        ezsServiceIds.tuCreditReportWithScore,
        ezsServiceIds.tuCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuCreditReportForEachOf3Bureaus,
        ezsServiceIds.tuCreditReport,
        ezsServiceIds.tuCanadaOneTimeCreditReportWithScore,
    ];

    const threeBureauIds = [
        ezsServiceIds.tuMonthlyCreditReportForEachOf3Bureaus,
        ezsServiceIds.tuMonthlyCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuQuarterlyCreditReportForEachOf3Bureaus,
        ezsServiceIds.tuQuarterlyCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuAnnualCreditReportForEachOf3Bureaus,
        ezsServiceIds.tuAnnualCreditReportForEachOf3BureausWithScore,
    ];

    const creditScoreServiceids = [
        ezsServiceIds.monthlyCreditScore,
        ezsServiceIds.quarterlyCreditScore,
        ezsServiceIds.tuDailyCreditScore,
        ezsServiceIds.tuMonthlyCreditScore,
        ezsServiceIds.tuQuarterlyCreditScore,
        ezsServiceIds.tuAnnualCreditScore,
        ezsServiceIds.tuCanadaCreditScore,
        idfServiceIds.threeBureauCreditScores,
        idfServiceIds.transUnionCreditScore,
        ezsServiceIds.tuCreditReportWithScore,
        ezsServiceIds.tuDailyCreditReportWithScore,
        ezsServiceIds.tuMonthlyCreditReportWithScore,
        ezsServiceIds.tuQuarterlyCreditReportWithScore,
        ezsServiceIds.tuAnnualCreditReportWithScore,
        ezsServiceIds.experianAnnualCreditReportWithScore,
        ezsServiceIds.tuCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuMonthlyCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuQuarterlyCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuAnnualCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.experianAnnualCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuCanadaMonthlyCreditReportWithScore,
        ezsServiceIds.tuCanadaQuarterlyCreditReportWithScore,
        ezsServiceIds.tuCanadaAnnualCreditReportWithScore,
        ezsServiceIds.tuCanadaOneTimeCreditReportWithScore,
        ezsServiceIds.tuOneTimeCreditReportWithScore,
        ezsServiceIds.tuCanadaQuarterlyCreditReport,
    ];

    const isCreditScoreActive = useAreServicesActive(creditScoreServiceids);

    const isOneTimeCreditServiceActive = useAreServicesActive(oneTimeCreditServiceIds);

    const dailyRefreshableCreditServiceIds = [ezsServiceIds.tuDailyCreditReportWithScore, ezsServiceIds.tuDailyCreditReport];
    const isDailyRefreshableCreditServiceActive = useAreServicesActive(dailyRefreshableCreditServiceIds);

    const hasCombinationService = useAreServicesActive(threeBureauIds) && isDailyRefreshableCreditServiceActive ? true : false;

    let col = null;
    let isIDFUser = false;
    if (props.data != null) {
        isIDFUser = props.data.IsIDFUser;
        if (props.data.creditReport != null && props.data.creditReport.summary != null) {
            col = props.data.creditReport.summary.sort((a, b) => (a.id > b.id ? 1 : -1));
        }
    }

    var BureauLinkUrlMap = {
        TransUnion: "https://www.transunion.com/credit-disputes/dispute-your-credit",
        Equifax: "https://www.equifax.com/personal/disputes/",
        Experian: "https://www.experian.com/disputes/main.html",
    };

    function openPopup(creditBureau: string) {
        let url = "";
        if (creditBureau === "TransUnion") {
            url = BureauLinkUrlMap.TransUnion;
        } else if (creditBureau === "Equifax") {
            url = BureauLinkUrlMap.Equifax;
        } else if (creditBureau === "Experian") {
            url = BureauLinkUrlMap.Experian;
        }
        window.open(url, "_blank");
    }

    let bureauPermission1 = useBureauPermissionCreditReport();
    const handleSubmit = (e: any) => {
        if (isIDFUser) {
            setIsBusy(true);
            e.preventDefault();
            const requestBody = {
                "bureau-opt": getServiceKey(bureauPermission1),
            };
            httpClient
                .sendRequest("POST", `api/credit-monitoring/refreshCreditData`, requestBody)
                .then((data) => {
                    requestCreditData(false);
                    setIsBusy(false);
                })
                .catch(function (error) {
                    requestCreditData(false);
                    setIsBusy(false);
                });
        }

        if (isTUUser) {
            setIsBusy(true);
            refreshCredit();
        }
    };

    const refreshCallback = () => {
        if (isCreditScoreActive) {
            requestCreditData(true);
        }
        setIsBusy(false);
        setIsJustRefreshed(true);
        getUserService(true);
    };

    const getCadenceText = () => {
        const serviceCode = getActiveServiceCode(threeBureauIds);

        let cadenceText = "";
        switch (serviceCode) {
            case ezsServiceIds.tuMonthlyCreditReportForEachOf3BureausWithScore:
                cadenceText = "Monthly";
                break;
            case ezsServiceIds.tuQuarterlyCreditReportForEachOf3BureausWithScore:
                cadenceText = "Quarterly";
                break;
            case ezsServiceIds.tuAnnualCreditReportForEachOf3BureausWithScore:
                cadenceText = "Annual";
                break;
            default:
                cadenceText = "";
        }

        return cadenceText;
    };
    const reportSummaryHeader =
        hasCombinationService && props.view === "3b"
            ? "Three Bureau " + getCadenceText() + " Credit Report Summary"
            : props.view === "daily"
                ? "TransUnion Daily Report Summary"
                : tLang("credit.creditReport.tabs.creditSummarySection.title");

    let showRefreshScore = false;
    let today = getDate(new Date().setHours(0, 0, 0, 0));
    if (props.data != null) {
        let nextPull = props.data["next-update-date"] == null ? getDate(props.data["update-date"]) : getDate(props.data["next-update-date"]);
        let yearDiff = nextPull.diff(today, "years");

        if (nextPullInYears === -1) {
            setNextPullInYears(yearDiff);
        }

        let diff = today.diff(nextPull, "days");
        if (!isNaN(diff) && nextPull.diff(today, "days") <= 0 && props.data["tu-status"] !== "TU-Fulfill-Pull-Error") {
            showRefreshScore = true;
        } else {
            showRefreshScore = false;
        }
    }

    const printCreditReport = () => {
        const htmlMarkup = ReactDOMServer.renderToStaticMarkup(
            <PrintCreditReport
                data={props.data}
                isTUUser={props.isTUUser}
                isOneTimeCreditServiceActive={isOneTimeCreditServiceActive}
                reportSummaryHeader={reportSummaryHeader}
            />
        );
        let printWindow = window.open("", "_blank", "width=600,height=800");
        if (printWindow != null) {
            printWindow.document.head.innerHTML =
                '<title>Credit Report</title><link href="' +
                decodeURIComponent(encodeURIComponent(window.location.origin)) +
                '/app/css/_application/application.css" rel="stylesheet" /> <link rel="stylesheet" href="https://maxst.icons8.com/vue-static/landings/line-awesome/line-awesome/1.3.0/css/line-awesome.min.css"/>';
            printWindow.document.body.innerHTML = htmlMarkup;
            printWindow.focus();
            setTimeout(function () {
                if (printWindow != null) {
                    printWindow.print();
                }
            }, 2000);
        }
    };

    const showDisputeLink = (bureau: string) => {
        const creditReportFailedBureaus = props?.data?.creditReport?.failedBureaus ?? [];
        const lowerCaseFailedBureuNames = creditReportFailedBureaus.map(el => el.toLowerCase());
        if (!lowerCaseFailedBureuNames.includes(bureau.toLowerCase())) {
            return true;
        }
        return false;
    }

    const renderMessageScreen = () => {
        if (props.data && (props.data.IsIDFUser || isTUUser) && (props.data["tu-status"] === "tu-thin-error" || props.data["tu-status"] === "tu-down")) {
            return <CreditStatus status={props.data["tu-status"]} />;
        } else if (props.data && (props.data.IsIDFUser || isTUUser) && props.data.IsExpired) {
            return <CreditReportExpired creditTabName="report" />;
        } else {
            return renderScoreScreen();
        }
    };

    const renderScoreScreen = () => {
        if (props.data != null) {
            return (
                <div className="row between-xs">
                    <div className="col-xs col-gutter-vertical flex-grow-null">
                        {showRefreshScore === false ? (
                            <ul className="list-reset margin-bottom-s credit-report-dates">
                                <li>
                                    <em className="las la-calendar-check icon-inline"></em> <strong>{tLang("credit.creditReport.tabs.creditSummarySection.updatedLabel")}: </strong>
                                    {props.data["update-date"] && getFormattedDate(i18nService.get("Country"), props.data["update-date"])}
                                </li>
                                {(isIDFUser || isTUUser) && nextPullInYears < 2 && props.view !== "historical" && !props.isOneTimeReport && props.data["tu-status"] !== "TU-Fulfill-Pull-Error" ? (
                                    <li>
                                        <em className="las la-calendar-day icon-inline"></em> <strong>{tLang("credit.creditReport.tabs.creditSummarySection.nextUpdateAvailableLabel")}: </strong>
                                        {props.data["next-update-date"] && getFormattedDate(i18nService.get("Country"), props.data["next-update-date"])}
                                    </li>
                                ) : null}
                            </ul>
                        ) : (
                            <React.Fragment>
                                <ul className="list-reset margin-bottom-s credit-report-dates">
                                    <li>
                                        <em className="las la-calendar-check icon-inline"></em> <strong>{tLang("credit.creditReport.tabs.creditSummarySection.updatedLabel")}: </strong>
                                        {props.data["update-date"] && getFormattedDate(i18nService.get("Country"), props.data["update-date"])}
                                    </li>
                                </ul>
                                {isIDFUser || (isTUUser && !props?.data?.IsPastReport && !isJustRefreshed && !props.isOneTimeReport) ? (
                                    <button type="button" className="btn-default btn-primary" onClick={(e: any) => handleSubmit(e)}>
                                        <span className="btn-text">{tLang("credit.creditReport.tabs.creditSummarySection.refreshCreditReportLabel")}</span>
                                    </button>
                                ) : null}
                            </React.Fragment>
                        )}
                    </div>
                    <div className="col-xs col-gutter-vertical flex-grow-null">
                        <ul className="list-reset list-inline btn-list">
                            <li>
                                <button
                                    type="button"
                                    className="btn-default btn-secondary-outline"
                                    onClick={() => {
                                        printCreditReport();
                                        window.addToDataLayer({ event: "Print Credit Summary Report" });
                                    }}
                                >
                                    <span className="btn-icon">
                                        <em className="las la-print"></em>
                                    </span>
                                    <span className="btn-text">{tLang("credit.creditReport.tabs.creditSummarySection.printReportButton")}</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            );
        } else {
            return "";
        }
    };

    return (
        <React.Fragment>
            {isBusy ? (
                <div className="loading"></div>
            ) : (
                <div className="box offset-padding">
                    {col != null || props?.data?.IsExpired ? (
                        <React.Fragment>
                            <div className="box-header">
                                <div className="box-title-container">
                                    <div className="box-title-icon" aria-hidden="true">
                                        <RenderIcons iconName="file-invoice" />
                                    </div>
                                    <div className="box-title-text">
                                        <h2 className="box-title">{reportSummaryHeader}</h2>
                                    </div>
                                </div>

                                <div className="box-helper">{renderMessageScreen()}</div>
                                <div className="box-actions credit-nav">
                                    <CreditReportNavigation
                                        view={props.view}
                                        callback={props.toggleReportView}
                                        cadenceText={getCadenceText}
                                        hasCombinationService={hasCombinationService}
                                    />
                                </div>
                            </div>
                            <React.Fragment>
                                    {props?.data?.IsExpired === false && props.data["tu-status"] !== "tu-thin-error" && props.data["tu-status"] !== "tu-down" ? (
                                    <div className="box-content">
                                        <div className="table-container scroll">
                                            <table className="m-table-generic">
                                                <caption className="sr-only">Credit Summary Table</caption>
                                                <thead>
                                                    <tr>
                                                        <th scope="col">
                                                            <span className="sr-only">Item</span>
                                                        </th>
                                                        {col?.map((summary: ISummaryItems) => (
                                                            <th key={summary["credit-bureau"] + "names"} scope="col">
                                                                {summary["credit-bureau"] === "Merge" ? "Total" : summary["credit-bureau"]}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <th scope="row" className="table-label">
                                                            {tLang("credit.creditReport.tabs.creditSummarySection.accountsInfoSection.totalAccounts")}
                                                        </th>
                                                        {col?.map((summary: ISummaryItems) => (
                                                            <td key={summary["credit-bureau"] + "totalAccounts"}>{summary["total-accounts"]}</td>
                                                        ))}
                                                    </tr>
                                                    <tr>
                                                        <th scope="row" className="table-label">
                                                            {tLang("credit.creditReport.tabs.creditSummarySection.accountsInfoSection.openAccounts")}
                                                        </th>
                                                        {col?.map((summary: ISummaryItems) => (
                                                            <td key={summary["credit-bureau"] + "openAccounts"}>{summary["open-accounts"]}</td>
                                                        ))}
                                                    </tr>
                                                    <tr>
                                                        <th scope="row" className="table-label">
                                                            {tLang("credit.creditReport.tabs.creditSummarySection.accountsInfoSection.closedAccounts")}
                                                        </th>
                                                        {col?.map((summary: ISummaryItems) => (
                                                            <td key={summary["credit-bureau"] + "closedAccounts"}>{summary["closed-accounts"]}</td>
                                                        ))}
                                                    </tr>
                                                    <tr>
                                                        <th scope="row" className="table-label">
                                                            {tLang("credit.creditReport.tabs.creditSummarySection.accountsInfoSection.totalBalances")}
                                                        </th>
                                                        {col?.map((summary: ISummaryItems) => (
                                                            <td key={summary["credit-bureau"] + "totalBalances"}>{summary["total-balances"] ? formatCurrency(summary["total-balances"]) : ''}</td>
                                                        ))}
                                                    </tr>
                                                    <tr>
                                                        <th scope="row" className="table-label">
                                                            {tLang("credit.creditReport.tabs.creditSummarySection.accountsInfoSection.totalMonthlyPayments")}
                                                        </th>
                                                        {col?.map((summary: ISummaryItems) => (
                                                            <td key={summary["credit-bureau"] + "totalMonthlyPaymnents"}>
                                                                {summary["total-monthly-payments"] ? formatCurrency(summary["total-monthly-payments"]) : ''}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                    <tr>
                                                        <th scope="row" className="table-label">
                                                            {tLang("credit.creditReport.tabs.creditSummarySection.accountsInfoSection.delinquentAccounts")}
                                                        </th>
                                                        {col?.map((summary: ISummaryItems) => (
                                                            <td key={summary["credit-bureau"] + "deliquentAccounts"}>{summary["delinquent-accounts"]}</td>
                                                        ))}
                                                    </tr>
                                                    <tr>
                                                        <th scope="row" className="table-label">
                                                            {tLang("credit.creditReport.tabs.creditSummarySection.accountsInfoSection.inquiriesInTheLast2Years")}
                                                        </th>
                                                        {col?.map((summary: ISummaryItems) => (
                                                            <td key={summary["credit-bureau"] + "inquiriesLast2Years"}>{summary["inquiries-last-2-years"]}</td>
                                                        ))}
                                                    </tr>
                                                    <tr>
                                                        <th scope="row" className="table-label">
                                                            {tLang("credit.creditReport.tabs.creditSummarySection.accountsInfoSection.numberofPublicRecords")}
                                                        </th>
                                                        {col?.map((summary: ISummaryItems) => (
                                                            <td key={summary["credit-bureau"] + "numberOfPublicRecords"}>{summary["number-public-records"]}</td>
                                                        ))}
                                                    </tr>
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <th></th>
                                                        {col
                                                            ?.filter((it) => it.id === 1)
                                                            .map((summary: ISummaryItems) => (
                                                                <td></td>
                                                            ))}
                                                        {col
                                                            ?.filter((it) => it.id !== 1 && showDisputeLink(it["credit-bureau"]))
                                                            .map((summary: ISummaryItems) => (
                                                                <td key={summary["credit-bureau"] + "disputeForms"}>
                                                                    <button
                                                                        type="button"
                                                                        className="btn-default btn-no-padding btn-link btn-no-border "
                                                                        onClick={() => openPopup(summary["credit-bureau"])}
                                                                    >
                                                                        <span className="btn-text">
                                                                            <span className="sr-only">{summary["credit-bureau"]}</span> {tLang("credit.creditReport.tabs.creditSummarySection.accountsInfoSection.disputeForm")}
                                                                        </span>
                                                                    </button>
                                                                </td>
                                                            ))}
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </React.Fragment>
                        </React.Fragment>
                    ) : (
                        <></>
                    )}
                </div>
            )}
        </React.Fragment>
    );
};

export default CreditSummary;
